import { ScoreColorVariant } from '@vizit/constants';
import { cva } from '@vizit/theme';
import { ScoreStickerCardSize } from './types';

// The same timing the metric cells use, so every card in the row changes state in step.
const TIMING = 'duration-350 motion-reduce:transition-none';

// Three rows with equal outer tracks keep the score at the card's true centre no
// matter how tall the label above or the diff below happen to be.
const CARD_LAYOUT = 'grid grid-rows-[1fr_auto_1fr] justify-items-center shrink-0 border-2';

const cardClasses = cva({
  base: `${CARD_LAYOUT} transition-all ${TIMING}`,
  variants: {
    size: {
      [ScoreStickerCardSize.Lg]: 'h-[187px] w-[187px] py-8 shadow-lg-soft',
      [ScoreStickerCardSize.Md]: 'h-[155px] w-[155px] py-6 shadow-xs',
    },
    // The tier's surface, matching ScoreStickerV2, ringed by the tier's lighter edge.
    colorVariant: {
      [ScoreColorVariant.NotApplicable]: 'border-score-not-applicable bg-score-not-applicable',
      [ScoreColorVariant.VeryLow]: 'border-score-very-low bg-score-very-low',
      [ScoreColorVariant.Low]: 'border-score-low bg-score-low',
      [ScoreColorVariant.Moderate]: 'border-score-moderate bg-score-moderate',
      [ScoreColorVariant.High]: 'border-score-high bg-score-high',
      [ScoreColorVariant.VeryHigh]: 'border-score-very-high bg-score-very-high',
    },
    // Muted (breakdown open): the tint drops to the metric cells' white surface, and a
    // primary border keeps the card distinct from the borderless cells beside it.
    isMuted: {
      true: 'border-primary bg-primary',
      false: '',
    },
    isDisabled: {
      true: 'border-transparent bg-transparent shadow-none',
      false: '',
    },
  },
  defaultVariants: { size: ScoreStickerCardSize.Lg, isMuted: false, isDisabled: false },
});

// Loading: the same footprint and surface as the loaded card, holding the caption,
// score and diff as skeleton bars — matching the metric cells' loading treatment.
const skeletonClasses = cva({
  base: `${CARD_LAYOUT} border-transparent bg-primary`,
  variants: {
    size: {
      [ScoreStickerCardSize.Lg]: 'h-[187px] w-[187px] py-8 shadow-lg-soft',
      [ScoreStickerCardSize.Md]: 'h-[155px] w-[155px] py-6 shadow-xs',
    },
  },
  defaultVariants: { size: ScoreStickerCardSize.Lg },
});

const scoreTextClasses = cva({
  // Only the size morphs; colour snaps, exactly as the metric cells' numbers do.
  base: `font-mono font-bold leading-none transition-[font-size] ${TIMING}`,
  variants: {
    size: {
      [ScoreStickerCardSize.Lg]: 'text-display-xl',
      [ScoreStickerCardSize.Md]: 'text-display-lg',
    },
    colorVariant: {
      [ScoreColorVariant.NotApplicable]: 'text-score-not-applicable-strong',
      [ScoreColorVariant.VeryLow]: 'text-score-very-low-strong',
      [ScoreColorVariant.Low]: 'text-score-low-strong',
      [ScoreColorVariant.Moderate]: 'text-score-moderate-strong',
      [ScoreColorVariant.High]: 'text-score-high-strong',
      [ScoreColorVariant.VeryHigh]: 'text-score-very-high-strong',
    },
    // Muted matches the tertiary the metric cells fade their numbers to while the panel is open.
    isMuted: {
      true: 'text-tertiary',
      false: '',
    },
    // Same disabled gray the metric cells drop their numbers to, so the row dims as one.
    isDisabled: {
      true: 'text-fg-disabled-subtle',
      false: '',
    },
  },
  defaultVariants: { size: ScoreStickerCardSize.Lg, isMuted: false, isDisabled: false },
});

const labelClasses = cva({
  base: 'self-start',
  variants: {
    colorVariant: {
      [ScoreColorVariant.NotApplicable]: 'text-score-not-applicable-label',
      [ScoreColorVariant.VeryLow]: 'text-score-very-low-label',
      [ScoreColorVariant.Low]: 'text-score-low-label',
      [ScoreColorVariant.Moderate]: 'text-score-moderate-label',
      [ScoreColorVariant.High]: 'text-score-high-label',
      [ScoreColorVariant.VeryHigh]: 'text-score-very-high-label',
    },
    // Muted drops the tier surface, so the caption returns to the metric cells' quaternary.
    isMuted: {
      true: 'text-quaternary',
      false: '',
    },
    // Same disabled caption token the metric cells use, so the row dims as one.
    isDisabled: {
      true: 'text-fg-disabled-subtle',
      false: '',
    },
  },
  defaultVariants: { isMuted: false, isDisabled: false },
});

export { cardClasses, labelClasses, scoreTextClasses, skeletonClasses };
